import { useEffect, useId, useRef, useState } from "react";
import type { FormEvent } from "react";
import { CHAT_MAX_LENGTH } from "@grundbesitz/shared";
import type { RoomView } from "@grundbesitz/shared";
import { Button } from "../ui/Button";
import { colorHex } from "../../lib/format";
import { useStore } from "../../lib/store";

const formatTime = (at: number) => new Date(at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });

/** Compact lobby chat. Text is rendered through React only, never as HTML. */
export function LobbyChat({ room }: { room: RoomView }) {
  const sendChat = useStore((s) => s.sendChat);
  const inputId = useId();
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);
  const messages = room.chat.slice(-100);

  useEffect(() => {
    const el = listRef.current;
    if (el && stickToBottom.current) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  const onScroll = () => {
    const el = listRef.current;
    if (el) stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setSending(true);
    const ack = await sendChat(trimmed);
    setSending(false);
    if (ack.ok) {
      setText("");
      stickToBottom.current = true;
    }
  };

  return (
    <section aria-labelledby="chat-title">
      <h2 id="chat-title" className="font-display text-xl font-semibold text-ink">
        Chat
      </h2>
      <div
        ref={listRef}
        onScroll={onScroll}
        role="log"
        aria-live="polite"
        aria-label="Chatverlauf"
        tabIndex={0}
        className="mt-3 h-48 overflow-y-auto rounded-xl border border-line bg-surface px-3 py-2"
      >
        {messages.length === 0 ? (
          <p className="text-sm text-ink-2">Noch keine Nachrichten.</p>
        ) : (
          <ul className="space-y-1.5">
            {messages.map((m) => {
              const author = room.members.find((x) => x.id === m.playerId);
              return m.kind === "system" ? (
                <li key={m.id} className="text-sm italic text-ink-2">
                  {m.text}
                </li>
              ) : (
                <li key={m.id} className="text-sm text-ink [overflow-wrap:anywhere]">
                  <span
                    aria-hidden="true"
                    className="mr-1.5 inline-block size-2.5 rounded-full align-baseline"
                    style={{ background: colorHex(author?.color) }}
                  />
                  <span className="font-semibold">{m.name}</span>
                  <span className="ml-1.5 text-xs text-ink-2">{formatTime(m.at)}</span>
                  <br />
                  {m.text}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <form onSubmit={submit} className="mt-2 flex gap-2">
        <label htmlFor={inputId} className="sr-only">
          Nachricht
        </label>
        <input
          id={inputId}
          value={text}
          maxLength={CHAT_MAX_LENGTH}
          autoComplete="off"
          enterKeyHint="send"
          placeholder="Nachricht schreiben"
          onChange={(e) => setText(e.target.value)}
          className="h-11 min-w-0 flex-1 rounded-lg border border-line bg-surface px-3 text-ink placeholder:text-ink-2"
        />
        <Button type="submit" disabled={text.trim() === "" || sending}>
          Senden
        </Button>
      </form>
    </section>
  );
}
